/**
 * Persisted, UID-scoped campaign-detail drafts — the per-creator
 * editor values that survive page reloads:
 *
 *   - scriptDraftByCreator: pasted-but-not-yet-submitted script body
 *   - contentUrlDraft:      typed-but-not-submitted content URL
 *   - insightInputs:        live-insight number-field drafts
 *   - creatorDecisions:     local approve/reject indicator before the API call
 *
 * Storage key includes the Firebase UID so two browser sessions for
 * different users on the same machine don't share drafts.
 *
 * Wave 2a (Zustand split). Companion stores: `useCampaignUiStore`
 * (session-only UI state), `useCampaignEditorStore` (transient
 * open/closed flags + review feedback).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth } from '@discovr/common';

/**
 * UID-scoped localStorage key. Falls back to a generic key if called
 * before auth resolves — Zustand rehydrates on mount, by which point
 * auth.currentUser is usually set, so the practical fallback only
 * fires on the very first paint for a signed-out user (whose drafts
 * we'd discard on sign-in anyway).
 */
function getPersistName(role: 'brand' | 'admin'): string {
  const uid = auth.currentUser?.uid;
  return uid
    ? `discovr-${role}-campaign-drafts-${uid}`
    : `discovr-${role}-campaign-drafts`;
}

export type CreatorDecision = 'approved' | 'rejected' | null;

export interface CampaignDraftsState {
  scriptDraftByCreator: Record<string, string>;
  contentUrlDraft: Record<string, string>;
  insightInputs: Record<string, Record<string, string>>;
  creatorDecisions: Record<string, CreatorDecision>;

  setScriptDraft: (creatorId: string, draft: string) => void;
  setContentUrlDraft: (creatorId: string, url: string) => void;
  setInsightInputs: (creatorId: string, inputs: Record<string, string>) => void;
  setCreatorDecisions: (
    update:
      | Record<string, CreatorDecision>
      | ((prev: Record<string, CreatorDecision>) => Record<string, CreatorDecision>),
  ) => void;
  clearDrafts: () => void;
}

const initialDrafts: Omit<
  CampaignDraftsState,
  | 'setScriptDraft'
  | 'setContentUrlDraft'
  | 'setInsightInputs'
  | 'setCreatorDecisions'
  | 'clearDrafts'
> = {
  scriptDraftByCreator: {},
  contentUrlDraft: {},
  insightInputs: {},
  creatorDecisions: {},
};

/**
 * Shared session-only (non-persisted) drafts store.
 *
 * Tab components in @discovr/ui import this directly. It uses the same
 * CampaignDraftsState interface as the persisted store so shared tabs work
 * without knowing which app they're in. Apps that want persistence create
 * their own role-scoped store via createDraftsStore(role).
 */
export const useCampaignDraftsStore = create<CampaignDraftsState>((set) => ({
  ...initialDrafts,
  setScriptDraft: (creatorId, draft) =>
    set((s) => ({ scriptDraftByCreator: { ...s.scriptDraftByCreator, [creatorId]: draft } })),
  setContentUrlDraft: (creatorId, url) =>
    set((s) => ({ contentUrlDraft: { ...s.contentUrlDraft, [creatorId]: url } })),
  setInsightInputs: (creatorId, inputs) =>
    set((s) => ({ insightInputs: { ...s.insightInputs, [creatorId]: inputs } })),
  setCreatorDecisions: (update) =>
    set((s) => ({
      creatorDecisions: typeof update === 'function' ? update(s.creatorDecisions) : update,
    })),
  clearDrafts: () => set(initialDrafts),
}));

/**
 * Create the role-scoped persisted drafts store. Apps call this once at
 * module load (one per role) and re-export the hook. Splitting by role keeps
 * brand and admin drafts in separate localStorage keys.
 */
export function createDraftsStore(role: 'brand' | 'admin') {
  return create<CampaignDraftsState>()(
    persist(
      (set) => ({
        ...initialDrafts,
        setScriptDraft: (creatorId, draft) =>
          set((s) => ({
            scriptDraftByCreator: { ...s.scriptDraftByCreator, [creatorId]: draft },
          })),
        setContentUrlDraft: (creatorId, url) =>
          set((s) => ({ contentUrlDraft: { ...s.contentUrlDraft, [creatorId]: url } })),
        setInsightInputs: (creatorId, inputs) =>
          set((s) => ({ insightInputs: { ...s.insightInputs, [creatorId]: inputs } })),
        setCreatorDecisions: (update) =>
          set((s) => ({
            creatorDecisions:
              typeof update === 'function' ? update(s.creatorDecisions) : update,
          })),
        clearDrafts: () => set(initialDrafts),
      }),
      {
        name: getPersistName(role),
        partialize: (state) =>
          ({
            scriptDraftByCreator: state.scriptDraftByCreator,
            contentUrlDraft: state.contentUrlDraft,
            insightInputs: state.insightInputs,
            creatorDecisions: state.creatorDecisions,
          }) as Partial<CampaignDraftsState>,
      },
    ),
  );
}
